/**
 * rtKnowledgeModule.js — Справочник по радиохирургии прямо в интерфейсе
 * Mikhmel Cornerstone4D DICOM Viewer Pro — Этап 2, часть 5
 *
 * ЗАЧЕМ
 *   Термины GTV, CTV, PTV, метод Кавальери, геометрические искажения МР —
 *   это специальные знания лучевого терапевта и медицинского физика.
 *   Врач другой специальности, глядя на кнопку «Построить PTV», не обязан
 *   понимать, что это и почему отступ именно такой. Поэтому у каждого
 *   неочевидного места стоит знак «?», а по нему — короткая, но честная
 *   статья.
 *
 * УСТРОЙСТВО
 *   • helpIcon(id) — вставляет значок «?» рядом с элементом интерфейса
 *   • по клику открывается статья
 *   • кнопка «Справочник» открывает всё содержание списком
 *   • статьи дублируются в общий словарь inlineHelp, чтобы их находил
 *     встроенный поиск по справке
 */

import { log } from '../utils/logger.js';
import { registerAPIs } from '../core/viewerAPI.js';

export const RT_KB_VERSION = '1.2';

// ═══════════════════════════════════════════════════════════════
// СТАТЬИ
// ═══════════════════════════════════════════════════════════════

export const KB = {
  'units': {
    title: 'Единицы: объём, площадь, длина — почему мл, мм² и мм',
    group: 'Измерение',
    text: `В панели три разные величины, и меряются они в разных единицах — это не
      ошибка, а разная «размерность».<br><br>
      <b>Объём мишени — в миллилитрах (мл).</b> Это кубическая величина.
      1&nbsp;мл = 1&nbsp;см³ = <b>1000&nbsp;мм³</b>. То есть 0.61&nbsp;мл — это
      610&nbsp;мм³. В лучевой терапии объём принято мерить в мл (то же самое, что
      см³ и «кубики»/cc), поэтому панель показывает мл, а не мм³ — по сути это
      одно и то же, просто привычная клиническая единица.<br><br>
      <b>Наибольшее сечение — в мм².</b> Это <i>площадь</i> самого крупного среза
      мишени, величина двумерная, поэтому квадратные миллиметры. Это не объём —
      её показывают как ориентир «насколько крупная опухоль в самом широком месте».<br><br>
      <b>Эквивалентный диаметр и габариты — в мм.</b> Это <i>длины</i> (одномерные):
      диаметр шара того же объёма и размеры описывающего прямоугольника. Поэтому
      просто миллиметры.<br><br>
      Короткая памятка: длина → мм, площадь → мм², объём → мл&nbsp;(=&nbsp;см³&nbsp;=&nbsp;1000&nbsp;мм³).`,
  },
  'srs': {
    title: 'Стереотаксическая радиохирургия (СРХ)',
    group: 'Основы',
    text: `Однократное (реже 2–5 сеансов) прицельное облучение мишени высокой дозой
      с резким спадом дозы за её пределами. «Хирургия» здесь условна: разреза нет,
      эффект достигается точностью наведения.<br><br>
      Ключевое отличие от обычной лучевой терапии — <b>точность порядка миллиметра</b>.
      Она достигается жёсткой фиксацией головы (стереотаксическая рама или
      термопластическая маска), контролем положения по снимкам непосредственно
      перед сеансом и множеством пучков, сходящихся в мишени.<br><br>
      Аппараты: Гамма-нож (кобальтовые источники), КиберНож (робот с линейным
      ускорителем), линейные ускорители с конусами или многолепестковым коллиматором.`,
  },
  'gtv-ctv-ptv': {
    title: 'GTV, CTV, PTV, OAR — объёмы мишени',
    group: 'Основы',
    text: `Четыре понятия, на которых стоит всё планирование (определения ICRU 50/62/83).<br><br>
      <b>GTV</b> — Gross Tumor Volume, видимая опухоль. Ровно то, что различимо на снимке
      и что вы обводите.<br><br>
      <b>CTV</b> — Clinical Target Volume, GTV плюс зона вероятного микроскопического
      распространения, которого на снимке не видно. Для менингиомы CTV обычно совпадает
      с GTV: она растёт компактно, раздвигая ткани, а не прорастая в них.<br><br>
      <b>PTV</b> — Planning Target Volume, CTV плюс <i>геометрический</i> запас.
      К биологии опухоли отношения не имеет: это поправка на неточность укладки,
      микродвижения пациента, погрешность аппарата и ошибку совмещения снимков.
      Именно PTV накрывают дозой, чтобы CTV гарантированно её получил.<br><br>
      <b>OAR</b> — Organs At Risk, критические структуры рядом. Для основания черепа это
      зрительные нервы и хиазма, ствол мозга, черепные нервы, улитка, гипофиз.`,
  },
  'srs-margins': {
    title: 'Почему в радиохирургии отступ 0–2 мм',
    group: 'Основы',
    text: `При обычной лучевой терапии отступ CTV→PTV берут 5–10 мм. В стереотаксисе —
      от 0 до 2 мм, чаще 1 мм, а при рамной методике на Гамма-ноже нередко ноль.<br><br>
      Причина в том, что отступ покрывает <b>технические</b> неточности, а в СРХ их
      целенаправленно устраняют: голову жёстко фиксируют, положение проверяют по снимкам
      прямо перед сеансом, аппарат откалиброван до долей миллиметра.<br><br>
      Обратная сторона: каждый лишний миллиметр отступа — это заметно больше облучённой
      здоровой ткани (объём растёт как куб радиуса). У основания черепа критические
      структуры лежат вплотную, поэтому отступ там сводят к минимуму. Величина отступа —
      клиническое решение под конкретный аппарат и локализацию.`,
  },
  'reformat': {
    title: 'Можно ли работать по реформатированным срезам',
    group: 'Точность',
    text: `<b>Оконтуривать — можно. Считать реформат геометрической основой плана — нельзя.</b><br><br>
      Контур хранится не в пикселях картинки, а в пациентских координатах — миллиметрах
      внутри Frame of Reference. Обводите вы на аксиальном реформате или на корональном,
      точки пересчитываются в одни и те же трёхмерные миллиметры. Реформатирование
      геометрию не искажает. Поэтому настоящие планирующие станции позволяют рисовать
      в любой из трёх проекций — это нормальная практика.<br><br>
      Что реформат теряет — резкость: пиксели интерполированы, граница выглядит глаже,
      чем в данных. Критично при анизотропной серии: если срезы по 3 мм, то на сагиттальном
      реформате «пиксель» в одном направлении 3 мм, и оконтуривать по нему — самообман.
      Для объёмных последовательностей (3D T1 BRAVO, MPRAGE, VIBE) с тонкими срезами
      реформаты пригодны.<br><br>
      А вот геометрической основой плана служит КТ — см. статью об искажениях МР.`,
  },
  'mr-distortion': {
    title: 'Искажения МРТ и почему основа плана — КТ',
    group: 'Точность',
    text: `МР-изображение геометрически <b>не точно</b>. Искажения дают нелинейность
      градиентов, неоднородность основного поля и различия магнитной восприимчивости
      тканей. В центре поля зрения смещение невелико, но к периферии достигает 1–2 мм,
      а у границ кость—воздух (пазухи, пирамиды височных костей) бывает и больше.<br><br>
      Для радиохирургии, где весь смысл в субмиллиметровой точности, это неприемлемо.
      Поэтому классическая схема такова: <b>геометрическая основа — КТ</b>, у которой
      искажений нет; на ней же задаётся стереотаксическая система координат (рама или
      реперы). МРТ жёстко совмещается с КТ и используется для того, для чего незаменима —
      увидеть границы опухоли. План строится в координатах КТ.<br><br>
      Для <i>измерения объёма при наблюдении</i> это ограничение не так существенно:
      искажение в пару миллиметров на периферии мало влияет на объём компактного
      образования, а при сравнении исследований с одного аппарата систематическая
      ошибка одинакова и в разнице сокращается.`,
  },
  'cavalieri': {
    title: 'Как считается объём: метод Кавальери',
    group: 'Измерение',
    text: `Объём = сумма площадей контуров × расстояние между срезами.<br><br>
      Это принцип Кавальери, и его главное достоинство в том, что он <b>не делает
      никаких предположений о форме</b>. Метод точен для любой фигуры — шара, подковы,
      опухоли с отростками — и тем точнее, чем чаще обведены срезы.<br><br>
      Поэтому одного контура мало: у плоской фигуры нет толщины. Нужно минимум два
      среза, чтобы появилось расстояние, на которое умножается площадь.<br><br>
      Обводить каждый срез подряд не обязательно: программа считает по фактическому
      шагу между обведёнными и заполняет промежутки. Но крайние срезы, где опухоль
      ещё видна, обвести нужно обязательно — иначе верх и низ отрежутся, и объём
      выйдет заниженным.<br><br>
      <b>Считать обводки бессмысленно — важно покрытие.</b> Проверено на шаре
      известного объёма: очаг 17 мм на срезах 6 мм виден всего дважды, и двух
      контуров хватает — ошибка 6%. А два контура из семнадцати при срезах 1 мм
      дают занижение на 85%: посчитаны два тонких слоя вместо опухоли. Разница не
      в числе обводок, а в том, дошли ли они до краёв.<br><br>
      Программа проверяет это сама: у полностью обведённого очага крайние сечения
      — его шапочки, и они обязаны быть маленькими. Насколько маленькими, считается
      геометрией. Если крайнее сечение почти равно наибольшему, обводку, скорее
      всего, бросили на полпути, и об этом будет сказано в отчёте. На толстых
      срезах проверка признаётся бессильной и молчит — там отличить «очаг
      кончился» от «врач остановился» по контурам действительно нельзя.`,
  },
  'three-planes': {
    title: 'Почему три проекции хуже стопки контуров',
    group: 'Измерение',
    text: `Кажется логичным: обвести опухоль в аксиальной, корональной и сагиттальной
      плоскости и получить точный объём. На деле — наоборот.<br><br>
      Три взаимно перпендикулярных сечения <b>не определяют трёхмерное тело однозначно</b>:
      бесконечно много разных фигур дают одни и те же три проекции. Из них можно вывести
      только формулу эллипсоида V = π/6 · a · b · c, то есть <i>предположить</i>, что
      опухоль правильной формы.<br><br>
      Сравнение на модельных фигурах (эталон — численное интегрирование):<br>
      • шар: стопка −1.0%, три проекции −5.4%<br>
      • дольчатая: стопка −1.4%, три проекции +3.0%<br>
      • <b>с хвостом вдоль оболочки: стопка +2.2%, три проекции +41.5%</b><br>
      • подкова: стопка +2.1%, три проекции −14.6%<br><br>
      Последние две формы типичны для менингиомы. Поэтому объём считается по одной
      стопке параллельных контуров, а оценка по габаритам показана рядом только
      как перекрёстная проверка.`,
  },
  'why-volume': {
    title: 'Почему объём, а не диаметр',
    group: 'Измерение',
    text: `При наблюдении менингиомы главный вопрос один: растёт она или нет.<br><br>
      Линейный размер отвечает на него плохо. Опухоль неправильной формы, и прибавка
      2 мм по одной оси может означать и +10%, и +40% объёма — в зависимости от того,
      где именно она выросла. Кроме того, измерение диаметра сильно зависит от того,
      на каком срезе и под каким углом его провели.<br><br>
      Объём лишён этих недостатков: это интегральная величина, она не зависит от выбора
      среза и учитывает изменение формы. Именно поэтому в наблюдательных исследованиях
      менингиом динамику оценивают по объёму, а типичный порог значимого роста —
      прирост порядка 10–15% или превышение погрешности повторного измерения.<br><br>
      Практический совет: обводите одинаково от исследования к исследованию —
      систематическая ошибка тогда сокращается в разнице, и динамика будет достоверной.`,
  },
  'rtstruct': {
    title: 'Что такое RTSTRUCT',
    group: 'Форматы',
    text: `DICOM RT Structure Set (SOP Class 1.2.840.10008.5.1.4.1.1.481.3) — стандартный
      файл, в котором хранятся контуры структур: мишени, критические органы, внешний контур.<br><br>
      Внутри: список ROI с именами и цветами, а для каждой — набор плоских контуров,
      заданных координатами точек в миллиметрах (тег ContourData) в системе координат
      пациента. Поэтому контуры не привязаны к конкретной картинке и открываются в любой
      станции, где загружена та же серия.<br><br>
      RTSTRUCT — один из четырёх RT-объектов DICOM: RTSTRUCT (контуры), RTPLAN (параметры
      облучения), RTDOSE (трёхмерное распределение дозы), RTIMAGE (снимки проверки укладки).<br><br>
      Файл, который создаёт эта программа, содержит ваши контуры и объём. Его можно
      загрузить обратно, передать врачу или сохранить в архив исследования.`,
  },
  'auto-limits': {
    title: 'Почему автовыделение не всегда работает',
    group: 'Измерение',
    text: `Автоматическое выделение растит область от точки клика, включая соседние
      воксели с похожей яркостью. Это работает, когда у опухоли есть <b>граница по
      интенсивности</b>.<br><br>
      У менингиомы основания черепа её местами нет вовсе: опухоль накапливает контраст
      так же, как прилежащая твёрдая мозговая оболочка, венозные синусы и костный мозг.
      Алгоритм не может различить то, что неразличимо в данных.<br><br>
      Признак безнадёжной ситуации виден по скану допуска: объём прыгает сразу с нуля
      на десятки миллилитров, минуя всякое плато. Значит не существует порога, при
      котором выделяется компактная мишень. Программа это распознаёт и предупреждает.<br><br>
      В таких случаях мишень оконтуривают вручную — так же поступают и в настоящих
      планирующих станциях. Автоматика там служит черновым наброском, а границу
      проводит врач по анатомии.`,
  },
  'dural-tail': {
    title: 'Дуральный хвост менингиомы',
    group: 'Основы',
    text: `Полоска утолщённой, интенсивно накапливающей контраст твёрдой мозговой
      оболочки, отходящая от основного узла менингиомы. Встречается примерно в 60–70%
      случаев и считается характерным (хотя и не строго специфичным) признаком.<br><br>
      Гистологически хвост не всегда содержит опухолевые клетки — часто это реактивное
      утолщение и гиперваскуляризация оболочки. Поэтому вопрос, включать ли его в мишень,
      решается индивидуально: при радиохирургии обычно ограничиваются узлом и прилежащим
      участком оболочки, чтобы не расширять объём облучения без необходимости.<br><br>
      Для измерения объёма важно решить это один раз и <b>придерживаться одного правила
      во всех исследованиях</b> — иначе динамика будет отражать не рост опухоли, а
      изменение вашего подхода к обводке.`,
  },
  'draw-howto': {
    title: 'Как обводить опухоль вручную',
    group: 'Обводка',
    text: `Зажмите левую кнопку и обведите опухоль — как отпустите, контур
      замкнётся сам.<br><br>
      Дальше <b>прокрутите колесо на 2–3 среза</b> и обведите ту же опухоль там.
      Так 5–10 раз, сверху донизу: от среза, где опухоль только появилась, до
      среза, где она пропала.<br><br>
      <b>Почему одного контура мало.</b> Объём считается по расстоянию между
      срезами — у одиночного контура нет толщины, считать не из чего. Два
      контура дают грубую оценку, пять-десять — рабочую.<br><br>
      Ошиблись — «Отменить контур» убирает последний, «Удалить всё» стирает
      обводку целиком.`,
  },

  'slice-step': {
    title: 'Шаг между срезами и точность объёма',
    group: 'Измерение',
    text: `Чем чаще обведены срезы, тем точнее объём. Практическое правило: по высоте
      опухоли должно получиться <b>не меньше 6–10 контуров</b>.<br><br>
      Для образования 10–12 мм при срезах 0.6 мм это означает обводить примерно каждый
      второй срез — шаг около 1.2 мм. Пропускать по 3–4 мм для такой опухоли много:
      форма между контурами восстанавливается линейно, и реальные изгибы теряются.<br><br>
      Для крупных образований (30 мм и больше) шаг 2–3 мм вполне достаточен.<br><br>
      Программа следит за этим сама: если пропуск между соседними контурами превышает
      3.5 мм, она об этом предупредит.`,
  },

  // ═══════ Этап 3: совмещение ═══════
  'fusion': {
    title: 'Совмещение КТ и МРТ: зачем и почему опорная — КТ',
    group: 'Совмещение',
    text: `Планирование опирается на два исследования сразу, и роли у них разные.<br><br>
      <b>КТ — геометрический эталон.</b> Рентгеновская трубка и детектор жёстко
      закреплены, поэтому расстояния на КТ соответствуют действительности с точностью
      доли миллиметра. Кроме того, только КТ даёт электронную плотность тканей, без
      которой невозможен расчёт дозы.<br><br>
      <b>МРТ — эталон видимости.</b> Менингиома на КТ часто едва отличима от оболочек и
      кости, а на T1 с контрастом видна отчётливо, с границами и дуральным хвостом.<br><br>
      <b>Но МРТ геометрически врёт.</b> Неоднородность основного поля, нелинейность
      градиентов и магнитная восприимчивость тканей смещают изображение обычно на
      1–2 мм, а по краям поля обзора и у границ кость/воздух — сильнее. Для мишени
      размером 10 мм это ошибка в десятки процентов объёма.<br><br>
      Поэтому порядок такой: <b>мишень рисуют по МРТ, а координаты берут с КТ</b>.
      Совмещение (fusion) переносит одно в систему координат другого. Опорным
      (неподвижным) всегда берут КТ — подвинуть следует то, что искажено.<br><br>
      Программа совмещает по <b>взаимной информации</b>: она не сравнивает яркости
      напрямую (у КТ и МРТ они не связаны), а ищет положение, при котором яркость на
      одном снимке максимально предсказывает яркость на другом. Это стандартный метод
      для разномодального совмещения.<br><br>
      <b>Совмещение обязательно проверяют глазами.</b> Пролистайте срезы при
      полупрозрачном наложении и сверьте костные ориентиры: спинку турецкого седла,
      пирамиды височных костей, наружные слуховые проходы. Если они расходятся —
      подправьте вручную кнопками докрутки.`,
  },

  // ═══════ Этап 4: доза ═══════
  'isodose': {
    title: 'Изодозы и предписанная доза',
    group: 'Доза',
    text: `<b>Изодоза</b> — линия (в 3D — поверхность), соединяющая точки с одинаковой
      дозой. Это в точности то же, что горизонталь на топографической карте, только
      вместо высоты — доза.<br><br>
      Дозу принято обозначать в процентах от максимума в плане. <b>Предписанная
      (краевая) доза</b> — та, которой обязана быть покрыта вся мишень; изодозу, по
      которой её задают, называют предписанной изодозой.<br><br>
      На гамма-ноже традиционно предписывают на <b>50%</b> изодозу, на линейном
      ускорителе и CyberKnife — обычно на <b>70–85%</b>. Отсюда важное следствие:
      предписание «13 Гр на 50%» означает, что <b>в центре мишени доза около 26 Гр</b>.
      Такая неоднородность внутри мишени в радиохирургии не дефект, а приём: она даёт
      более крутой спад дозы за пределами мишени.<br><br>
      Именно крутизна спада, а не абсолютная доза, отличает радиохирургию от обычной
      лучевой терапии. Смысл всей затеи — уместить высокую дозу в мишень и обрушить её
      до безопасной на расстоянии нескольких миллиметров.<br><br>
      В программе изодозы читаются из файла RTDOSE, рассчитанного планирующей станцией.
      <b>Программа дозу не считает</b> — она её показывает.`,
  },
  'dvh': {
    title: 'DVH — гистограмма доза–объём',
    group: 'Доза',
    text: `Изодозы показывают дозу срез за срезом. DVH сворачивает всё
      трёхмерное распределение по одной структуре в единственную кривую.<br><br>
      Используется <b>кумулятивная</b> форма: по горизонтали доза, по вертикали — <b>доля
      объёма структуры, получающая НЕ МЕНЕЕ этой дозы</b>. Кривая всегда начинается в
      100% слева и убывает.<br><br>
      Как читать:<br>
      • <b>для мишени</b> идеал — кривая, идущая горизонтально на 100% и обрывающаяся
      отвесно у предписанной дозы: весь объём получил своё и ни капли больше;<br>
      • <b>для критического органа</b> идеал — кривая, падающая к нулю как можно раньше:
      значит высокой дозы в органе почти нет.<br><br>
      Ключевые точки кривой:<br>
      • <b>D95</b> — доза, которую получают минимум 95% объёма. Показатель того, не
      осталось ли в мишени «холодных» участков.<br>
      • <b>D98 и D2</b> — по ICRU 83 это «почти минимум» и «почти максимум». Их берут
      вместо истинных Dmin/Dmax, потому что те определяются одним-единственным вокселем
      и потому неустойчивы к шуму.<br>
      • <b>V100</b> — какая доля мишени получила полную предписанную дозу. Это и есть
      покрытие.<br><br>
      <b>Важное ограничение этой программы.</b> DVH считается по вокселям дозовой сетки,
      попавшим внутрь ваших контуров, а контуры должны лежать <b>стопкой параллельных
      срезов одной проекции</b>. Три взаимно перпендикулярных контура (быстрый способ
      оценки объёма) для DVH не годятся: программа возьмёт только одну плоскость и
      честно об этом предупредит.`,
  },
  'coverage': {
    title: 'Покрытие, конформность и градиент',
    group: 'Доза',
    text: `Три числа, которыми оценивают качество радиохирургического плана.<br><br>
      <b>1. Покрытие (V100).</b> Доля мишени, получившая предписанную дозу. Стремятся
      к 95–100%. Недопокрытие означает, что часть опухоли облучена слабее, чем задумано.<br><br>
      <b>2. Конформность</b> — насколько форма дозового облака повторяет форму мишени.<br>
      Простой индекс RTOG: <code>CI = PIV / TV</code>, где TV — объём мишени, PIV — объём,
      охваченный предписанной изодозой. Идеал 1. Изъян в том, что он не замечает
      смещения: облако нужного размера, но сдвинутое мимо мишени, даст CI = 1.<br>
      Поэтому пользуются индексом Паддика:
      <code>CI = (TV<sub>PIV</sub>)² / (TV × PIV)</code>, где TV<sub>PIV</sub> — объём
      мишени, реально накрытый предписанной изодозой. Он штрафует и за лишний объём, и
      за промах. Идеал 1, на практике хорошими считают значения выше 0.7–0.8.<br><br>
      <b>3. Градиентный индекс</b> — как быстро доза спадает за мишенью:
      <code>GI = PIV<sub>50%</sub> / PIV</code>, то есть во сколько раз объём половинной
      изодозы больше объёма предписанной. Чем меньше, тем круче спад; типичные хорошие
      значения около 2.5–3.0. Именно этот показатель отвечает за сохранность здоровой
      ткани вокруг.<br><br>
      Конформность и градиент — величины <b>соперничающие</b>: облако можно плотно
      обжать по мишени ценой пологого спада, и наоборот. Работа планировщика в том,
      чтобы найти между ними разумное равновесие.`,
  },
  'constraints': {
    title: 'Дозовые пределы для критических структур (справочно)',
    group: 'Доза',
    text: `<b>Это справочный материал, а не рекомендация к лечению.</b> Дозу назначает
      лучевой терапевт вместе с нейрохирургом, по конкретной клинической ситуации.
      Цифры приведены, чтобы понимать, о чём идёт речь в плане и почему план выглядит
      именно так.<br><br>
      Для <b>однократного</b> радиохирургического сеанса в литературе приводят:<br><br>
      • <b>Зрительный путь</b> (нервы и хиазма). Рабочая группа HyTEC рекомендует
      максимальную точечную дозу <b>10 Гр</b> при отсутствии облучения в прошлом — это
      около 0.4% риска лучевой оптической нейропатии. По их же модели 12 Гр
      соответствуют примерно 1% риска, 14 Гр — около 2%. При повторном облучении риск
      возрастает примерно десятикратно.<br><br>
      • <b>Здоровая ткань мозга.</b> Ключевой показатель — <b>V12Гр</b>, объём мозга,
      получивший 12 Гр и больше. По сводным данным HyTEC 5 см³ соответствуют примерно
      10% риска симптомного лучевого некроза, 10 см³ — около 15%, свыше 15 см³ — около
      20%. Отсюда практическое стремление удержать V12Гр ниже 5–10 см³.<br><br>
      • <b>Ствол мозга</b> — обычно ограничивают максимум около 12–15 Гр, а при
      подлежащей вплотную мишени идут на осознанный компромисс.<br><br>
      • <b>Улитка</b> — для сохранения слуха стремятся к средней дозе не выше 4 Гр.<br><br>
      Для сравнения: <b>краевая доза при менингиоме WHO I</b> обычно составляет
      12–16 Гр за один сеанс, чаще 12–14 Гр. Именно поэтому мишень рядом со зрительным
      нервом — трудный случай: лечебная доза для опухоли выше, чем переносимая для
      нерва, и вопрос решается либо крутизной спада дозы, либо разбиением на несколько
      фракций.<br><br>
      Пределы пересматриваются по мере накопления данных; в разных центрах приняты
      несколько разные значения.`,
  },
};

// ═══════════════════════════════════════════════════════════════
// Значок «?» и всплывающая статья
// ═══════════════════════════════════════════════════════════════

/** HTML-значок «?», открывающий статью. Вставляется прямо в разметку панели. */
export function helpIcon(id) {
  // Размер и цвет задаёт класс .rt-help-q (index.html) — на токенах темы,
  // поэтому значок меняется вместе с оформлением приложения.
  return `<span class="rt-help-q" data-kb="${id}" title="Что это такое?" role="button" tabindex="0">?</span>`;
}

function _popupEl() {
  let el = document.getElementById('rt-kb-popup');
  if (!el) {
    el = document.createElement('div');
    el.id = 'rt-kb-popup';
    el.className = 'rt-modal';
    document.body.appendChild(el);
    el.addEventListener('click', (e) => { if (e.target === el) el.classList.remove('open'); });
    // Escape закрывает — привычка, которую ждут от любого модального окна
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') el.classList.remove('open');
    });
  }
  return el;
}

/** Открыть статью справочника. */
export function showArticle(id) {
  const a = KB[id];
  if (!a) { log.warn('[RT-KB] Нет статьи:', id); return; }
  const el = _popupEl();
  el.innerHTML = `
    <div class="rt-modal-box">
      <div class="rt-modal-head">
        <span>${a.title}</span>
        <button id="rt-kb-x" class="rt-modal-x" title="Закрыть" aria-label="Закрыть">×</button>
      </div>
      <div class="rt-modal-body">${a.text}</div>
      <div class="rt-modal-foot">
        <button id="rt-kb-all" class="rt-btn">Весь справочник</button>
        <button id="rt-kb-ok" class="rt-btn rt-btn--primary">Понятно</button>
      </div>
    </div>`;
  el.classList.add('open');
  el.querySelector('#rt-kb-x').addEventListener('click', () => el.classList.remove('open'));
  el.querySelector('#rt-kb-ok').addEventListener('click', () => el.classList.remove('open'));
  el.querySelector('#rt-kb-all').addEventListener('click', () => showContents());
}

/** Оглавление справочника. */
export function showContents() {
  const el = _popupEl();
  const groups = {};
  for (const [id, a] of Object.entries(KB)) {
    (groups[a.group] ||= []).push({ id, ...a });
  }
  const body = Object.entries(groups).map(([g, items]) => `
    <div style="margin-bottom:14px;">
      <div class="rt-sec-title" style="margin-bottom:6px;">${g}</div>
      ${items.map(i => `<button class="rt-kb-item rt-btn" data-kb="${i.id}"
        style="justify-content:flex-start;margin-top:5px;">${i.title}</button>`).join('')}
    </div>`).join('');

  el.innerHTML = `
    <div class="rt-modal-box">
      <div class="rt-modal-head">
        <span>Справочник по радиохирургии</span>
        <button id="rt-kb-x" class="rt-modal-x" title="Закрыть" aria-label="Закрыть">×</button>
      </div>
      <div class="rt-modal-body">${body}</div>
    </div>`;
  el.classList.add('open');
  el.querySelector('#rt-kb-x').addEventListener('click', () => el.classList.remove('open'));
  el.querySelectorAll('.rt-kb-item').forEach(d =>
    d.addEventListener('click', () => showArticle(d.dataset.kb)));
}

/** Делегированный обработчик кликов по всем значкам «?». */
function _bindIcons() {
  if (document.body._rtKbBound) return;
  document.body._rtKbBound = true;
  document.body.addEventListener('click', (e) => {
    const q = e.target.closest?.('.rt-help-q');
    if (!q) return;
    e.preventDefault();
    e.stopPropagation();
    showArticle(q.dataset.kb);
  }, true);
}

export function initRTKnowledge() {
  try {
    _bindIcons();
    registerAPIs({
      rtHelp: showArticle,
      rtHelpContents: showContents,
    });
    log.info(`[RT-KB] Справочник загружен — ${Object.keys(KB).length} статей, ${RT_KB_VERSION}`);
  } catch (e) {
    log.error('[RT-KB] init failed:', e);
  }
}

export default { initRTKnowledge, showArticle, showContents, helpIcon, KB };
